import React, { useEffect, useState, useMemo } from "react";

// ──────────────────────────────────────────────────────
// AdminFormAssets.jsx — 서식자료실 관리 (업로드·수정·활성·삭제)
//   /api/tools/forms/admin/* — admin_token Bearer (특송운임 관리 패턴 미러)
// ──────────────────────────────────────────────────────

const S = {
  card: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16, background: "#fff" },
  h: { fontSize: 15, fontWeight: 700, marginBottom: 10 },
  th: { textAlign: "left", padding: "6px 10px", borderBottom: "2px solid #e5e7eb", fontSize: 12.5, whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 13, verticalAlign: "middle" },
  input: { padding: "8px 10px", fontSize: 13.5, borderRadius: 7, border: "1px solid #d1d5db", fontFamily: "inherit", boxSizing: "border-box" },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 7, padding: "9px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnSm: { background: "#fff", color: "#111827", border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  msgOk: { marginTop: 8, fontSize: 13, color: "#137333" },
  msgErr: { marginTop: 8, fontSize: 13, color: "#b91c1c" },
};
const CAT_SUGGEST = ["수출입신고", "협정적용", "요건확인", "감면", "위임·계약", "기타"];
const CARRIERS = ["공통", "UPS", "FEDEX", "DHL"];

function auth(json) {
  const t = localStorage.getItem("admin_token");
  return { ...(json ? { "Content-Type": "application/json" } : {}),
    ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

export default function AdminFormAssets({ API_BASE_URL }) {
  const base = `${API_BASE_URL || ""}/api/tools/forms/admin`;
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  // 업로드 폼
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("기타");
  const [carrier, setCarrier] = useState("공통");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetch(`${base}/list`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((d) => setItems(d.items || []))
      .catch((e) => setErr(String(e.message || e)));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadForm = async () => {
    if (!file) { setMsg({ ok: false, text: "파일을 선택하세요." }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("carrier", carrier);
      fd.append("note", note);
      const r = await fetch(base, { method: "POST", headers: auth(false), body: fd });
      const d = await r.json();
      if (!r.ok) setMsg({ ok: false, text: "업로드 실패: " + (d.errorCode || r.status) });
      else {
        setMsg({ ok: true, text: `등록 완료 — [${d.category}] ${d.title}` });
        setFile(null); setTitle(""); setNote("");
        document.getElementById("form-file-input").value = "";
        load();
      }
    } catch (e) {
      setMsg({ ok: false, text: "네트워크 오류" });
    } finally { setBusy(false); }
  };

  const patch = async (id, fields) => {
    const r = await fetch(`${base}/${id}`, {
      method: "PUT", headers: auth(true), body: JSON.stringify(fields) });
    if (r.ok) load();
    else window.alert("수정 실패: HTTP " + r.status);
  };
  const del = async (x) => {
    if (!window.confirm(`[${x.category}] ${x.title} 서식을 삭제할까요? (파일도 삭제됩니다)`)) return;
    const r = await fetch(`${base}/${x.id}`, { method: "DELETE", headers: auth() });
    if (r.ok) load();
    else window.alert("삭제 실패: HTTP " + r.status);
  };

  const cats = useMemo(
    () => [...new Set([...CAT_SUGGEST, ...items.map((x) => x.category)])], [items]);

  return (
    <div>
      {err && <div style={S.msgErr}>목록 조회 실패: {err} (관리자 로그인 확인)</div>}

      <div style={S.card}>
        <div style={S.h}>서식 업로드 (hwp·doc·xlsx·pdf·zip 등, 20MB 이하)</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input id="form-file-input" type="file"
            onChange={(e) => setFile(e.target.files[0] || null)}
            style={{ ...S.input, padding: 6 }} />
          <input placeholder="표시 제목 (비우면 파일명)" value={title}
            onChange={(e) => setTitle(e.target.value)} style={{ ...S.input, width: 220 }} />
          <input list="form-cats" value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="카테고리" style={{ ...S.input, width: 150 }} />
          <datalist id="form-cats">
            {cats.map((c) => <option key={c} value={c} />)}
          </datalist>
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)}
            style={{ ...S.input, width: 100 }} title="캐리어 구분">
            {CARRIERS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="비고 (선택)" value={note}
            onChange={(e) => setNote(e.target.value)} style={{ ...S.input, width: 200 }} />
          <button style={S.btn} disabled={busy} onClick={uploadForm}>
            {busy ? "업로드 중…" : "업로드"}
          </button>
        </div>
        {msg && <div style={msg.ok ? S.msgOk : S.msgErr}>{msg.text}</div>}
      </div>

      <div style={S.card}>
        <div style={S.h}>서식 목록 ({items.length})</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={S.th}>캐리어</th>
            <th style={S.th}>카테고리</th><th style={S.th}>제목</th><th style={S.th}>파일</th>
            <th style={S.th}>다운로드</th><th style={S.th}>상태</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} style={!x.active ? { opacity: 0.45 } : undefined}>
                <td style={S.td}>
                  <select value={x.carrier || "공통"}
                    onChange={(e) => patch(x.id, { carrier: e.target.value })}
                    style={{ ...S.input, width: 90, padding: "4px 6px" }}>
                    {CARRIERS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </td>
                <td style={S.td}>
                  <input defaultValue={x.category} style={{ ...S.input, width: 110, padding: "4px 8px" }}
                    onBlur={(e) => e.target.value !== x.category && patch(x.id, { category: e.target.value })} />
                </td>
                <td style={S.td}>
                  <input defaultValue={x.title} style={{ ...S.input, width: 220, padding: "4px 8px" }}
                    onBlur={(e) => e.target.value !== x.title && patch(x.id, { title: e.target.value })} />
                </td>
                <td style={{ ...S.td, fontSize: 12, color: "#6b7280" }}>
                  {x.orig_name} <span style={{ color: "#9ca3af" }}>({x.ext})</span>
                </td>
                <td style={S.td}>{x.download_count}</td>
                <td style={S.td}>
                  <button style={S.btnSm} onClick={() => patch(x.id, { active: !x.active })}>
                    {x.active ? "게시중 → 숨김" : "숨김 → 게시"}
                  </button>
                </td>
                <td style={S.td}>
                  <button style={{ ...S.btnSm, color: "#b91c1c", borderColor: "#fca5a5" }}
                    onClick={() => del(x)}>삭제</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td style={S.td} colSpan={7}>등록된 서식이 없습니다 — 위에서 업로드하세요.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
